import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { CookieNotice } from './CookieNotice';
import { SiteFooter } from './Footer';
import { SiteHeader } from './Header';

const TITLES: Record<string, string> = {
  '/': 'cevop — Ring a sale. Stock and money follow.',
  '/pos': 'cevop POS — Selling should be the easy part',
  '/business': 'cevop Business — Stock, money, and reports',
  '/pricing': 'Pricing — cevop',
  '/billing': 'Billing — cevop',
  '/solutions': 'Solutions — cevop',
  '/help': 'Help centre — cevop',
  '/guides': 'Guides — cevop',
  '/faq': 'FAQ — cevop',
  '/blog': 'Journal — cevop',
  '/stories': 'How it is used — cevop',
  '/security': 'Security — cevop',
  '/about': 'About — cevop',
  '/contact': 'Contact — cevop',
  '/privacy': 'Privacy Policy — cevop',
  '/terms': 'Terms of Service — cevop',
};

export function Layout({ children }: { children: ReactNode }) {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const nested =
      (pathname.startsWith('/help/') && 'Help — cevop') ||
      (pathname.startsWith('/blog/') && 'Journal — cevop') ||
      (pathname.startsWith('/stories/') && 'How it is used — cevop') ||
      undefined;
    document.title = TITLES[pathname] ?? nested ?? 'Page not found — cevop';
  }, [pathname]);

  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  useEffect(() => {
    const main = document.getElementById('main');
    if (!main) return;

    main.classList.remove('mk-page');
    void main.offsetWidth;
    main.classList.add('mk-page');

    const sections = main.querySelectorAll<HTMLElement>('section, article');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof IntersectionObserver === 'undefined') {
      sections.forEach((el) => el.classList.add('is-in'));
      return;
    }

    sections.forEach((el) => el.classList.remove('is-in'));
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' },
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [pathname]);

  return (
    <div className="flex min-h-full flex-col bg-surface">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:rounded-md focus:bg-brand focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-brand-on"
      >
        Skip to content
      </a>
      <SiteHeader />
      <main id="main" className="flex-1">
        {children}
      </main>
      <SiteFooter />
      <CookieNotice />
    </div>
  );
}
